import { useEffect, useRef, useState } from "react";
import { Bell } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { fmtDateTime } from "@/lib/utils";

function NotificationList({ items, unread, onMarkAllRead }) {
  return (
    <>
      {items.length === 0 && <div className="px-4 py-6 text-[12.5px] text-slate-400 text-center">No notifications yet.</div>}
      {items.map((n) => (
        <div key={n.id} className={`px-4 py-3 border-b border-slate-50 ${n.read ? "" : "bg-sky-50/60"}`} data-testid={`notification-item-${n.id}`}>
          <div className="text-[12.5px] font-semibold">{n.title}</div>
          <div className="text-[12px] text-slate-500 mt-0.5">{n.message}</div>
          <div className="text-[10.5px] text-slate-400 mt-1">{fmtDateTime(n.created_at)}</div>
        </div>
      ))}
    </>
  );
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const ref = useRef(null);
  const openRef = useRef(open);
  const wsRef = useRef(null);

  useEffect(() => { openRef.current = open; }, [open]);

  const load = () => api.get("/notifications").then((r) => setItems(r.data)).catch(() => {});

  // Real-time delivery over WebSocket is primary — new notifications land
  // the instant the backend creates them, no polling delay. Same
  // reconnect-with-backoff (capped 10s) + fallback-to-polling pattern as
  // ChatPanel.jsx's live chat: if the socket drops, retry a few times; if
  // it never manages to connect at all (proxy/firewall blocking upgrades),
  // fall back to a 5s poll for the life of the session so the bell still
  // stays live either way.
  useEffect(() => {
    load();
    let cancelled = false;
    let ws = null;
    let retryTimer = null;
    let pollTimer = null;
    let attempt = 0;
    let everConnected = false;

    const stopPolling = () => { if (pollTimer) { clearInterval(pollTimer); pollTimer = null; } };
    const startPolling = () => {
      if (pollTimer) return;
      pollTimer = setInterval(load, 5000);
    };

    const connect = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get("/auth/ws-token");
        if (cancelled) return;
        const wsBase = API.replace(/^http/, "ws");
        ws = new WebSocket(`${wsBase}/notifications/ws?token=${data.token}`);
        wsRef.current = ws;
        ws.onopen = () => { everConnected = true; attempt = 0; stopPolling(); };
        ws.onmessage = (evt) => {
          const notif = JSON.parse(evt.data);
          setItems((prev) => (prev.some((n) => n.id === notif.id) ? prev : [notif, ...prev]));
          // The dropdown/sheet already shows it live when open — a toast on
          // top would be redundant there; only surface the toast when the
          // user isn't already looking at the list.
          if (!openRef.current) toast(notif.title, { description: notif.message });
        };
        ws.onclose = () => {
          if (cancelled) return;
          wsRef.current = null;
          if (!everConnected && attempt >= 3) { startPolling(); return; }
          attempt += 1;
          const delay = Math.min(1000 * 2 ** attempt, 10000);
          retryTimer = setTimeout(connect, delay);
        };
        ws.onerror = () => ws && ws.close();
      } catch {
        if (!cancelled) startPolling();
      }
    };
    connect();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      stopPolling();
      if (ws) ws.close();
      wsRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // The mobile Sheet renders in a portal outside `ref`, and already has
    // its own overlay-driven outside-click/escape dismissal (Radix) — this
    // listener must ignore clicks inside it, or every tap inside the sheet
    // would register as "outside" and close it immediately.
    const onClick = (e) => {
      if (e.target.closest('[role="dialog"]')) return;
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const unread = items.filter((n) => !n.read).length;

  const markAllRead = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="notification-bell-button"
        className="relative w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500 transition-colors"
      >
        <Bell size={17} />
        {unread > 0 && <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500" data-testid="notification-unread-dot" />}
      </button>

      {/* Desktop: lightweight anchored dropdown, unchanged. */}
      {open && (
        <div className="hidden sm:block absolute right-0 mt-2 w-80 bg-white border border-slate-200 rounded-xl shadow-lg z-50 max-h-96 overflow-y-auto ss-scrollbar" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 sticky top-0 bg-white">
            <span className="text-[13px] font-semibold">Notifications</span>
            {unread > 0 && (
              <button onClick={markAllRead} data-testid="mark-all-read-button" className="text-[11.5px] text-sky-600 font-medium">
                Mark all read
              </button>
            )}
          </div>
          <NotificationList items={items} unread={unread} onMarkAllRead={markAllRead} />
        </div>
      )}

      {/* Mobile: full-width slide-over sheet. */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="sm:hidden w-full max-w-full p-0 flex flex-col" data-testid="notification-sheet">
          <SheetHeader className="flex-row items-center justify-between space-y-0 px-4 py-3.5 border-b border-slate-100">
            <SheetTitle className="text-[15px]">Notifications</SheetTitle>
            {unread > 0 && (
              <button onClick={markAllRead} data-testid="mark-all-read-button-mobile" className="text-[13px] text-sky-600 font-medium mr-6">
                Mark all read
              </button>
            )}
          </SheetHeader>
          <div className="flex-1 overflow-y-auto ss-scrollbar">
            <NotificationList items={items} unread={unread} onMarkAllRead={markAllRead} />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
